import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import type { PaginatedUsers, UserListItem } from '../services/types';
import { formatDate, subStatusClass, subStatusLabel } from '../utils/format';

export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PaginatedUsers | null>(null);
  const [loading, setLoading] = useState(true);

  const page = Number(searchParams.get('page') || '1');
  const q = searchParams.get('q') || '';
  const subscription_status = searchParams.get('subscription_status') || 'all';
  const tier = searchParams.get('tier') || '';
  const language = searchParams.get('language') || '';

  const [qInput, setQInput] = useState(q);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listUsers({
      page,
      page_size: 20,
      q: q || undefined,
      subscription_status: subscription_status !== 'all' ? subscription_status : undefined,
      tier: tier || undefined,
      language: language || undefined,
    });
    if (res.data) setData(res.data);
    setLoading(false);
  }, [page, q, subscription_status, tier, language]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = (e: React.FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (qInput) next.set('q', qInput);
    else next.delete('q');
    next.set('page', '1');
    setSearchParams(next);
  };

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== 'all') next.set(key, value);
    else next.delete(key);
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Пользователи</h1>

      <form onSubmit={applyFilters} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <input
          className="input"
          placeholder="Поиск: ID, username, имя…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          style={{ minWidth: 220 }}
        />
        <select
          className="select"
          value={subscription_status}
          onChange={(e) => setFilter('subscription_status', e.target.value)}
        >
          <option value="all">Все подписки</option>
          <option value="active">Активная</option>
          <option value="expired">Истекла</option>
          <option value="none">Нет</option>
        </select>
        <select className="select" value={tier} onChange={(e) => setFilter('tier', e.target.value)}>
          <option value="">Все tier</option>
          <option value="premium">Premium</option>
          <option value="default">Default</option>
        </select>
        <select className="select" value={language} onChange={(e) => setFilter('language', e.target.value)}>
          <option value="">Все языки</option>
          <option value="ru">RU</option>
          <option value="en">EN</option>
        </select>
        <button type="submit" className="btn btn-primary">
          Найти
        </button>
      </form>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && data && (
        <>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Найдено: {data.total} · страница {data.page} из {data.pages}
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Имя / Username</th>
                  <th>Tier</th>
                  <th>Подписка</th>
                  <th>До</th>
                  <th>Ключи</th>
                  <th>Язык</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((u: UserListItem) => (
                  <tr key={u.user_id}>
                    <td>
                      <Link className="row-link" to={`/users/${u.user_id}`}>
                        {u.user_id}
                      </Link>
                    </td>
                    <td>
                      {u.first_name ?? '—'}
                      {u.username ? ` @${u.username}` : ''}
                    </td>
                    <td>{u.subscription_tier ?? '—'}</td>
                    <td>
                      <span className={subStatusClass(u.subscription_status)}>
                        {subStatusLabel(u.subscription_status)}
                      </span>
                    </td>
                    <td>{formatDate(u.subscription_until)}</td>
                    <td>{u.active_keys_count}</td>
                    <td>{u.language}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              ← Назад
            </button>
            <span style={{ color: 'var(--text-muted)' }}>
              {data.page} / {data.pages}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page >= data.pages}
              onClick={() => setPage(page + 1)}
            >
              Вперёд →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
